import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Play, 
  Heart, 
  Share2, 
  MessageCircle, 
  Star, 
  Clock, 
  User,
  Loader2,
  Globe,
  FileText,
  MessageCircle as MessageIcon,
  Send,
  ThumbsUp,
  MoreVertical,
  X,
  UserPlus,
  UserCheck,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  BarChart3,
  Settings,
  Sparkles,
  Download,
  Tag
} from 'lucide-react'
import { useStoryStore } from '../stores/storyStore'
import worldSimulatorStorageService from '../services/worldSimulatorStorageService'
import playCreateStorageService from '../services/playCreateStorageService'
import { RealAIService } from '../services/realAiService'
import { useWorldSimulatorStore } from '../stores/worldSimulatorStore'
import toast from 'react-hot-toast'

// 点赞、评论和关注数据存储服务
const contentInteractionService = {
  // 获取点赞数据
  getLikes: (type, id) => {
    const key = `likes_${type}_${id}`
    const data = localStorage.getItem(key)
    return data ? JSON.parse(data) : { count: 0, liked: false }
  },
  
  // 切换点赞
  toggleLike: (type, id) => {
    const key = `likes_${type}_${id}`
    const current = contentInteractionService.getLikes(type, id)
    const updated = {
      count: current.liked ? current.count - 1 : current.count + 1,
      liked: !current.liked
    }
    localStorage.setItem(key, JSON.stringify(updated))
    return updated
  },
  
  // 获取评论
  getComments: (type, id) => {
    const key = `comments_${type}_${id}`
    const data = localStorage.getItem(key)
    return data ? JSON.parse(data) : []
  },
  
  // 添加评论
  addComment: (type, id, comment) => {
    const key = `comments_${type}_${id}`
    const comments = contentInteractionService.getComments(type, id)
    const newComment = {
      id: `comment_${Date.now()}`,
      userId: 'guest', // 后续可以从AuthService获取
      username: '游客',
      avatar: null,
      content: comment,
      timestamp: Date.now(),
      likes: 0
    }
    comments.push(newComment)
    localStorage.setItem(key, JSON.stringify(comments))
    return newComment
  },
  
  // 获取关注状态
  getFollowStatus: (creatorId) => {
    const key = `follow_${creatorId}`
    return localStorage.getItem(key) === 'true'
  },
  
  // 切换关注
  toggleFollow: (creatorId) => {
    const key = `follow_${creatorId}`
    const isFollowing = contentInteractionService.getFollowStatus(creatorId)
    localStorage.setItem(key, String(!isFollowing))
    return !isFollowing
  }
}

export const ContentDetail = () => {
  const { type, id } = useParams() // type: 'story' | 'character' | 'world'
  const navigate = useNavigate()
  
  const [content, setContent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [likes, setLikes] = useState({ count: 0, liked: false })
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [isSubmittingComment, setIsSubmittingComment] = useState(false)
  const [isFollowing, setIsFollowing] = useState(false)
  const [isCreator, setIsCreator] = useState(false) // 是否是创作者本人
  const [showCreatorMenu, setShowCreatorMenu] = useState(false) // 创作者菜单
  const [showStatsModal, setShowStatsModal] = useState(false) // 数据统计弹窗
  const [showEditDialog, setShowEditDialog] = useState(false) // 编辑对话框
  
  // 角色创建相关状态（仅世界类型）
  const [showCharacterCreation, setShowCharacterCreation] = useState(false)
  const [characterCreationMode, setCharacterCreationMode] = useState(null) // 'ai' | 'template'
  const { selectWorld } = useWorldSimulatorStore()
  
  const currentUserId = 'guest' // 后续可以从AuthService获取

  // 加载内容详情
  useEffect(() => {
    const loadContent = async () => {
      try {
        setIsLoading(true)
        let loadedContent = null

        switch (type) {
          case 'story':
            const stories = useStoryStore.getState().stories
            loadedContent = stories.find(s => s.id === id)
            break
          case 'character':
            loadedContent = await playCreateStorageService.getCharacterProfile(id)
            break
          case 'world':
            loadedContent = await worldSimulatorStorageService.getWorld(id)
            break
          default:
            toast.error('未知的内容类型')
            navigate('/workshop')
            return
        }

        if (!loadedContent) {
          toast.error('内容不存在')
          navigate('/workshop')
          return
        }

        setContent(loadedContent)
        
        // 如果是世界类型，选择世界
        if (type === 'world') {
          selectWorld(loadedContent)
        }
        
        // 判断是否是创作者
        const creatorId = loadedContent.creatorId || loadedContent.creator?.userId || 'default-user'
        setIsCreator(creatorId === currentUserId)
        
        // 加载关注状态
        if (creatorId && creatorId !== currentUserId) {
          const followStatus = contentInteractionService.getFollowStatus(creatorId)
          setIsFollowing(followStatus)
        }
        
        // 加载点赞数据
        const likesData = contentInteractionService.getLikes(type, id)
        setLikes(likesData)
        
        // 加载评论
        const commentsData = contentInteractionService.getComments(type, id)
        setComments(commentsData)
      } catch (error) {
        console.error('加载内容详情失败:', error)
        toast.error('加载失败')
        navigate('/workshop')
      } finally {
        setIsLoading(false)
      }
    }

    if (type && id) {
      loadContent()
    }
  }, [type, id, navigate])

  // 处理点赞
  const handleLike = () => {
    const updated = contentInteractionService.toggleLike(type, id)
    setLikes(updated)
    if (updated.liked) {
      toast.success('已点赞')
    } else {
      toast.success('已取消点赞')
    }
  }

  // 处理关注
  const handleFollow = () => {
    const creatorId = content.creatorId || content.creator?.userId || 'default-user'
    const newStatus = contentInteractionService.toggleFollow(creatorId)
    setIsFollowing(newStatus)
    if (newStatus) {
      toast.success('已关注作者')
    } else {
      toast.success('已取消关注')
    }
  }

  // 处理分享
  const handleShare = async () => {
    const url = window.location.href
    const title = content?.name || content?.title || '内容分享'
    const text = content?.description || content?.summary || ''

    try {
      if (navigator.share) {
        await navigator.share({
          title,
          text,
          url
        })
        toast.success('分享成功')
      } else {
        // 复制链接到剪贴板
        await navigator.clipboard.writeText(url)
        toast.success('链接已复制到剪贴板')
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        // 如果用户取消分享，不显示错误
        await navigator.clipboard.writeText(url)
        toast.success('链接已复制到剪贴板')
      }
    }
  }
  
  // 处理编辑 - 打开编辑对话框
  const handleEdit = () => {
    setShowEditDialog(true)
    setShowCreatorMenu(false)
  }
  
  // 处理下架/发布
  const handleTogglePublish = async () => {
    try {
      const isPublic = content.isPublic || content.status === 'published'
      const newStatus = !isPublic
      
      switch (type) {
        case 'story':
          const { updateStory } = useStoryStore.getState()
          await updateStory(id, { status: newStatus ? 'published' : 'draft' })
          break
        case 'character':
          await playCreateStorageService.saveCharacterProfile({
            ...content,
            isPublic: newStatus,
            status: newStatus ? 'published' : 'draft'
          })
          break
        case 'world':
          await worldSimulatorStorageService.updateWorld(id, {
            isPublic: newStatus,
            status: newStatus ? 'published' : 'draft'
          })
          break
        default:
          break
      }
      
      setContent(prev => ({
        ...prev,
        isPublic: newStatus,
        status: newStatus ? 'published' : 'draft'
      }))
      
      toast.success(newStatus ? '已发布到公开' : '已下架')
      setShowCreatorMenu(false)
    } catch (error) {
      console.error('操作失败:', error)
      toast.error('操作失败')
    }
  }
  
  // 处理删除
  const handleDelete = async () => {
    if (!confirm('确定要删除这个作品吗？此操作将移至回收站')) return
    
    try {
      switch (type) {
        case 'story':
          const { updateStory } = useStoryStore.getState()
          await updateStory(id, { status: 'trash', deletedAt: Date.now() })
          break
        case 'character':
          await playCreateStorageService.saveCharacterProfile({
            ...content,
            status: 'trash',
            deletedAt: Date.now()
          })
          break
        case 'world':
          await worldSimulatorStorageService.updateWorld(id, {
            status: 'trash',
            deletedAt: Date.now()
          })
          break
        default:
          break
      }
      
      toast.success('已移至回收站')
      navigate('/my-world')
    } catch (error) {
      console.error('删除失败:', error)
      toast.error('删除失败')
    }
  }
  
  // 处理导出
  const handleExportContent = () => {
    try {
      // 构建完整的导出数据，包含所有创建时的详细信息
      const exportData = {
        // 基本信息
        id: content.worldId || content.id,
        type: type,
        name: content.name || content.title,
        description: content.description,
        summary: content.summary,
        
        // 创建信息
        createdAt: content.createdAt,
        updatedAt: content.updatedAt,
        creatorId: content.creatorId,
        creator: content.creator,
        
        // 状态信息
        status: content.status,
        isPublic: content.isPublic,
        
        // 世界类型专属
        ...(type === 'world' && {
          preText: content.preText,
          postText: content.postText,
          prePrompt: content.prePrompt,
          worldBook: content.worldBook,
          systems: content.systems,
          quantitativeSystems: content.quantitativeSystems,
          regions: content.regions,
          characters: content.characters,
          language: content.language
        }),
        
        // 角色类型专属
        ...(type === 'character' && {
          personality: content.personality,
          background: content.background,
          avatar: content.avatar,
          firstMessage: content.firstMessage,
          exampleDialogues: content.exampleDialogues
        }),
        
        // 故事类型专属
        ...(type === 'story' && {
          genre: content.genre,
          nodes: content.nodes,
          edges: content.edges,
          startNodeId: content.startNodeId
        }),
        
        // 统计数据
        playCount: content.playCount || 0,
        rating: content.rating || 0,
        
        // 导出元数据
        exportedAt: new Date().toISOString(),
        exportVersion: '1.0'
      }
      
      // 生成文件名
      const fileName = `${content.name || content.title || 'content'}-${Date.now()}.json`
      
      // 创建并下载文件
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.click()
      URL.revokeObjectURL(url)
      
      toast.success('导出成功！')
    } catch (error) {
      console.error('导出失败:', error)
      toast.error('导出失败')
    }
  }

  // 提交评论
  const handleSubmitComment = async () => {
    if (!newComment.trim()) {
      toast.error('请输入评论内容')
      return
    }

    setIsSubmittingComment(true)
    try {
      const comment = contentInteractionService.addComment(type, id, newComment.trim())
      setComments([...comments, comment])
      setNewComment('')
      toast.success('评论已发布')
    } catch (error) {
      console.error('发布评论失败:', error)
      toast.error('发布失败')
    } finally {
      setIsSubmittingComment(false)
    }
  }

  // 处理角色创建完成
  const handleCharacterCreated = async (characterData) => {
    try {
      // 创建会话并直接跳转到体验页面
      const userId = 'guest' // 后续可以从AuthService获取
      const session = await worldSimulatorStorageService.createSession(id, userId)
      
      // 更新会话，添加角色信息
      await worldSimulatorStorageService.updateSession(session.sessionId, {
        playerCharacter: characterData
      })
      
      // 选择世界
      selectWorld(content)
      
      // 跳转到体验页面
      navigate(`/world-simulator/experience/${id}`, {
        state: { 
          sessionId: session.sessionId,
          playerCharacter: characterData
        }
      })
      
      toast.success('角色创建完成，开始体验！')
    } catch (error) {
      console.error('创建角色失败:', error)
      toast.error('创建角色失败')
    }
  }

  // 进入体验/对话
  const handleStart = () => {
    switch (type) {
      case 'story':
        navigate(`/player/${id}`)
        break
      case 'character':
        navigate(`/play-create/chat/${id}`)
        break
      case 'world':
        // 世界类型显示角色创建对话框
        setShowCharacterCreation(true)
        break
      default:
        break
    }
  }

  // 格式化时间
  const formatTime = (timestamp) => {
    if (!timestamp) return '未知'
    const date = new Date(timestamp)
    const now = new Date()
    const diff = now - date
    if (diff < 60000) return '刚刚'
    if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}天前`
    return date.toLocaleDateString('zh-CN')
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-dark-300">加载中...</p>
        </div>
      </div>
    )
  }

  if (!content) {
    return null
  }

  // 获取内容信息
  const getContentInfo = () => {
    switch (type) {
      case 'story':
        return {
          title: content.title,
          description: content.description || '暂无描述',
          image: content.coverImage || content.thumbnail,
          icon: FileText,
          startLabel: '创建角色并开始体验',
          meta: [
            { icon: Clock, text: `更新于 ${formatTime(content.updatedAt)}` },
            { icon: FileText, text: content.genre || '未分类' }
          ]
        }
      case 'character':
        return {
          title: content.name,
          description: content.description || '暂无描述',
          image: content.avatar,
          icon: MessageCircle,
          startLabel: '开始对话',
          meta: [
            { icon: User, text: content.personality || '未知性格' },
            { icon: Star, text: content.isPublic ? '已发布' : '草稿' }
          ]
        }
      case 'world':
        return {
          title: content.name,
          description: content.description || content.summary || '暂无描述',
          image: content.coverImage,
          icon: Globe,
          startLabel: '创建角色并开始体验',
          meta: [
            { icon: Play, text: `${content.playCount || 0} 次游玩` },
            { icon: Star, text: `${content.rating || 0} 评分` }
          ]
        }
      default:
        return null
    }
  }

  const info = getContentInfo()
  if (!info) return null

  const Icon = info.icon
  const startButtonClassName = [
    'w-full px-6 py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2',
    type === 'world'
      ? 'bg-gradient-to-r from-primary via-secondary to-primary text-white border-2 border-white/20 ring-2 ring-secondary/40 shadow-[0_18px_45px_rgba(244,114,182,0.45)] hover:ring-secondary/60 hover:border-secondary/60 hover:shadow-[0_22px_55px_rgba(244,114,182,0.6)] hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-secondary/70'
      : 'bg-gradient-to-r from-primary to-secondary text-white hover:shadow-lg hover:shadow-primary/50'
  ].join(' ')

  return (
    <div className="min-h-screen bg-dark-900">
      {/* 顶部导航栏 */}
      <div className="sticky top-16 z-40 bg-dark-900/95 backdrop-blur-sm border-b border-dark-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-dark-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <Icon className="w-5 h-5 text-primary" />
            <h1 className="text-xl font-bold text-white">{info.title}</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* 左侧：主要内容 */}
          <div className="lg:col-span-2 space-y-6">
            {/* 首图 */}
            <div className="relative w-full h-96 rounded-xl overflow-hidden bg-gradient-to-br from-primary/20 to-secondary/20">
              {info.image ? (
                <img
                  src={info.image}
                  alt={info.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextElementSibling.style.display = 'flex'
                  }}
                />
              ) : null}
              <div className={`absolute inset-0 flex items-center justify-center ${info.image ? 'hidden' : ''}`}>
                <Icon className="w-32 h-32 text-primary/30" />
              </div>
            </div>

            {/* 标题和操作栏 */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-white mb-2">{info.title}</h2>
                
                {/* 标签 */}
                {content.tags && content.tags.length > 0 && (
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <Tag className="w-4 h-4 text-primary" />
                    {content.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full border border-primary/20 hover:bg-primary/20 transition-colors cursor-pointer"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                
                <div className="flex items-center gap-4 text-sm text-dark-400">
                  {info.meta.map((meta, index) => (
                    <div key={index} className="flex items-center gap-1">
                      <meta.icon className="w-4 h-4" />
                      <span>{meta.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* 操作按钮 */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleLike}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    likes.liked
                      ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                      : 'bg-dark-800 text-dark-300 hover:text-white border border-dark-700'
                  }`}
                >
                  <Heart className={`w-5 h-5 ${likes.liked ? 'fill-current' : ''}`} />
                  <span>{likes.count}</span>
                </button>
                
                <button
                  onClick={handleShare}
                  className="flex items-center gap-2 px-4 py-2 bg-dark-800 text-dark-300 hover:text-white rounded-lg transition-colors border border-dark-700"
                >
                  <Share2 className="w-5 h-5" />
                  <span className="hidden sm:inline">分享</span>
                </button>
              </div>
            </div>

            {/* 内容介绍 */}
            <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
              <h3 className="text-xl font-bold text-white mb-4">内容介绍</h3>
              <p className="text-dark-300 leading-relaxed whitespace-pre-wrap">
                {info.description}
              </p>
            </div>

            {/* 世界类型的详细信息 */}
            {type === 'world' && content && (
              <>
                {/* 世界观设定 */}
                {content.preText && (
                  <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
                    <h3 className="text-xl font-bold text-white mb-4">世界观设定</h3>
                    <div className="text-dark-300 leading-relaxed whitespace-pre-wrap">
                      {content.preText}
                    </div>
                  </div>
                )}

                {/* 系统与规则 */}
                {(content.quantitativeSystems?.length > 0 || content.systems) && (
                  <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
                    <h3 className="text-xl font-bold text-white mb-4">系统与规则</h3>
                    {content.quantitativeSystems?.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-lg font-medium text-white mb-3">量化系统</h4>
                        <p className="text-sm text-dark-400 mb-3">
                          每一条对应一个会被数值追踪的世界规则，例如角色状态、资源或势力影响力。通过初始值、单位、范围描述可快速理解它的作用。
                        </p>
                        <div className="space-y-3">
                          {content.quantitativeSystems.map((sys, i) => (
                            <div key={i} className="p-4 bg-dark-900/50 rounded-lg border border-dark-700/60">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <p className="text-white font-semibold">
                                    {sys.name || `量化系统 ${i + 1}`}
                                  </p>
                                  {sys.description && (
                                    <p className="text-sm text-dark-300 mt-1 whitespace-pre-wrap">
                                      {sys.description}
                                    </p>
                                  )}
                                  {sys.purpose && !sys.description && (
                                    <p className="text-sm text-dark-300 mt-1 whitespace-pre-wrap">
                                      {sys.purpose}
                                    </p>
                                  )}
                                  {sys.breakpoint && (
                                    <p className="text-xs text-dark-400 mt-2">
                                      触发条件：{sys.breakpoint}
                                    </p>
                                  )}
                                </div>
                                <div className="flex flex-col items-end text-sm text-primary gap-1">
                                  {sys.initial !== undefined && (
                                    <span>初始值：{sys.initial}</span>
                                  )}
                                  {(sys.min !== undefined || sys.max !== undefined) && (
                                    <span className="text-xs text-dark-300">
                                      范围：{sys.min ?? '未知'} ~ {sys.max ?? '未知'}
                                    </span>
                                  )}
                                  {sys.unit && (
                                    <span className="text-xs text-dark-300">
                                      单位：{sys.unit}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {content.systems && (
                      <div className="space-y-3">
                        {typeof content.systems === 'string' ? (
                          <div className="text-dark-300 leading-relaxed whitespace-pre-wrap">
                            {content.systems}
                          </div>
                        ) : (
                          Object.entries(content.systems).map(([key, value]) => (
                            <div key={key} className="bg-dark-900/50 rounded-lg p-4 border border-dark-600">
                              <h4 className="text-lg font-medium text-white mb-2">{key}</h4>
                              <p className="text-dark-300 text-sm whitespace-pre-wrap">
                                {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* 地区设定 */}
                {content.regions && content.regions.length > 0 && (
                  <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
                    <h3 className="text-xl font-bold text-white mb-4">主要地区</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {content.regions.map((region, i) => (
                        <div key={i} className="bg-dark-900/50 rounded-lg p-4 border border-dark-600">
                          <h4 className="text-lg font-medium text-white mb-2">{region.name}</h4>
                          {region.description && (
                            <p className="text-dark-300 text-sm">{region.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 重要角色 */}
                {content.characters && content.characters.length > 0 && (
                  <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
                    <h3 className="text-xl font-bold text-white mb-4">重要角色</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {content.characters.map((character, i) => (
                        <div key={i} className="bg-dark-900/50 rounded-lg p-4 border border-dark-600">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-lg font-medium text-white">{character.name}</h4>
                            {character.role && (
                              <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded">
                                {character.role}
                              </span>
                            )}
                          </div>
                          {character.description && (
                            <p className="text-dark-300 text-sm">{character.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* 评论区域 */}
            <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
              <div className="flex items-center gap-2 mb-4">
                <MessageIcon className="w-5 h-5 text-primary" />
                <h3 className="text-xl font-bold text-white">评论 ({comments.length})</h3>
              </div>

              {/* 评论输入框 */}
              <div className="mb-6">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleSubmitComment()
                      }
                    }}
                    placeholder="写下你的评论..."
                    className="flex-1 px-4 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary"
                  />
                  <button
                    onClick={handleSubmitComment}
                    disabled={isSubmittingComment || !newComment.trim()}
                    className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    <span className="hidden sm:inline">发布</span>
                  </button>
                </div>
              </div>

              {/* 评论列表 */}
              <div className="space-y-4">
                {comments.length === 0 ? (
                  <div className="text-center py-8 text-dark-400">
                    <MessageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>还没有评论，快来发表第一条吧！</p>
                  </div>
                ) : (
                  comments.map((comment) => (
                    <div
                      key={comment.id}
                      className="bg-dark-700 rounded-lg p-4 border border-dark-600"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-white">{comment.username}</span>
                            <span className="text-xs text-dark-400">{formatTime(comment.timestamp)}</span>
                          </div>
                          <p className="text-dark-300 mb-2">{comment.content}</p>
                          <div className="flex items-center gap-4">
                            <button className="flex items-center gap-1 text-dark-400 hover:text-primary transition-colors text-sm">
                              <ThumbsUp className="w-4 h-4" />
                              <span>{comment.likes || 0}</span>
                            </button>
                            <button className="text-dark-400 hover:text-primary transition-colors text-sm">
                              回复
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* 右侧：操作面板 */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-4">
              {/* 开始体验按钮（用户和创作者都显示） */}
              <button
                onClick={handleStart}
                className={startButtonClassName}
              >
                <Play className="w-5 h-5" />
                {info.startLabel}
              </button>

              {/* 创作者管理区域（仅创作者可见） */}
              {isCreator && (
                <div className="bg-dark-800 rounded-xl p-4 border border-dark-700">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-white flex items-center gap-2">
                      <Settings className="w-4 h-4 text-secondary" />
                      创作者管理
                    </h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      content.isPublic || content.status === 'published'
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                        : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    }`}>
                      {content.isPublic || content.status === 'published' ? '已发布' : '草稿'}
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    <button
                      onClick={handleEdit}
                      className="w-full px-4 py-2.5 text-left text-white hover:bg-dark-700 rounded-lg transition-colors flex items-center gap-2"
                    >
                      <Edit className="w-4 h-4 text-primary" />
                      <span className="text-sm">编辑作品</span>
                    </button>
                    
                    <button
                      onClick={handleTogglePublish}
                      className="w-full px-4 py-2.5 text-left text-white hover:bg-dark-700 rounded-lg transition-colors flex items-center gap-2"
                    >
                      {content.isPublic || content.status === 'published' ? (
                        <>
                          <EyeOff className="w-4 h-4 text-yellow-400" />
                          <span className="text-sm">下架作品</span>
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 text-green-400" />
                          <span className="text-sm">发布作品</span>
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => setShowStatsModal(true)}
                      className="w-full px-4 py-2.5 text-left text-white hover:bg-dark-700 rounded-lg transition-colors flex items-center gap-2"
                    >
                      <BarChart3 className="w-4 h-4 text-cyan-400" />
                      <span className="text-sm">数据分析</span>
                    </button>
                    
                    <button
                      onClick={handleExportContent}
                      className="w-full px-4 py-2.5 text-left text-white hover:bg-dark-700 rounded-lg transition-colors flex items-center gap-2"
                    >
                      <Download className="w-4 h-4 text-green-400" />
                      <span className="text-sm">导出作品</span>
                    </button>
                    
                    <button
                      onClick={handleDelete}
                      className="w-full px-4 py-2.5 text-left text-red-400 hover:bg-dark-700 rounded-lg transition-colors flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="text-sm">删除作品</span>
                    </button>
                  </div>
                  
                  {content.createdAt && (
                    <div className="mt-3 pt-3 border-t border-dark-700 text-xs text-dark-400">
                      创建于 {formatTime(content.createdAt)}
                    </div>
                  )}
                </div>
              )}

              {/* 作者信息卡片（仅用户可见） */}
              {!isCreator && (
                <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
                  <h4 className="text-sm font-bold text-dark-300 mb-4">作者</h4>
                  <div
                    className="flex items-center gap-3 mb-4 cursor-pointer hover:bg-dark-700/50 p-2 rounded-lg transition-colors"
                    onClick={() => {
                      // 后续可以跳转到作者主页
                      toast.info('作者主页功能开发中')
                    }}
                  >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-medium">
                        {content.creator?.username || content.creator?.name || '匿名创作者'}
                      </div>
                      <div className="text-dark-400 text-xs">
                        点击查看主页
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <button
                      onClick={handleFollow}
                      className={`w-full py-2 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                        isFollowing
                          ? 'bg-dark-700 text-white hover:bg-dark-600'
                          : 'bg-primary hover:bg-primary-dark text-white'
                      }`}
                    >
                      {isFollowing ? (
                        <>
                          <UserCheck className="w-4 h-4" />
                          已关注
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4" />
                          关注作者
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => {
                        // 跳转到作者作品页
                        toast.info('查看作者作品页功能开发中')
                      }}
                      className="w-full py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <Globe className="w-4 h-4" />
                      查看作者作品
                    </button>
                  </div>
                </div>
              )}

              {/* 统计信息（用户和创作者都显示） */}
              <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
                <h4 className="text-lg font-bold text-white mb-4">统计信息</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-dark-300">点赞数</span>
                    <span className="text-white font-semibold">{likes.count}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-dark-300">评论数</span>
                    <span className="text-white font-semibold">{comments.length}</span>
                  </div>
                  {type === 'world' && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-dark-300">游玩次数</span>
                        <span className="text-white font-semibold">{content.playCount || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-dark-300">评分</span>
                        <span className="text-white font-semibold">{content.rating || 0}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 编辑对话框（创作者可见） */}
      {isCreator && showEditDialog && (
        <EditContentDialog
          content={content}
          type={type}
          onClose={() => setShowEditDialog(false)}
          onSave={async (updatedContent, publishType) => {
            try {
              // 更新内容
              switch (type) {
                case 'story':
                  const { updateStory } = useStoryStore.getState()
                  await updateStory(id, {
                    ...updatedContent,
                    status: publishType === 'publish' ? 'published' : 'draft',
                    isPublic: publishType === 'publish',
                    updatedAt: Date.now()
                  })
                  break
                case 'character':
                  await playCreateStorageService.saveCharacterProfile({
                    ...content,
                    ...updatedContent,
                    status: publishType === 'publish' ? 'published' : 'draft',
                    isPublic: publishType === 'publish',
                    updatedAt: Date.now()
                  })
                  break
                case 'world':
                  await worldSimulatorStorageService.updateWorld(id, {
                    ...updatedContent,
                    status: publishType === 'publish' ? 'published' : 'draft',
                    isPublic: publishType === 'publish',
                    updatedAt: Date.now()
                  })
                  break
              }
              
              // 重新加载内容
              const reloadedContent = type === 'story' 
                ? useStoryStore.getState().stories.find(s => s.id === id)
                : type === 'character'
                ? await playCreateStorageService.getCharacterProfile(id)
                : await worldSimulatorStorageService.getWorld(id)
              
              setContent(reloadedContent)
              setShowEditDialog(false)
              
              toast.success(publishType === 'publish' ? '已发布更新' : '已保存到草稿')
            } catch (error) {
              console.error('保存失败:', error)
              toast.error('保存失败')
            }
          }}
        />
      )}

      {/* 数据统计弹窗（创作者可见） */}
      {isCreator && showStatsModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-xl p-8 max-w-2xl w-full mx-4 border border-dark-600 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-primary" />
                数据统计
              </h2>
              <button
                onClick={() => setShowStatsModal(false)}
                className="p-2 hover:bg-dark-700 rounded transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="space-y-6">
              {/* 总览 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-dark-900/50 rounded-lg p-4 border border-dark-600">
                  <div className="text-dark-300 text-sm mb-1">总点赞</div>
                  <div className="text-2xl font-bold text-primary">{likes.count}</div>
                </div>
                <div className="bg-dark-900/50 rounded-lg p-4 border border-dark-600">
                  <div className="text-dark-300 text-sm mb-1">总评论</div>
                  <div className="text-2xl font-bold text-secondary">{comments.length}</div>
                </div>
                {type === 'world' && (
                  <>
                    <div className="bg-dark-900/50 rounded-lg p-4 border border-dark-600">
                      <div className="text-dark-300 text-sm mb-1">游玩次数</div>
                      <div className="text-2xl font-bold text-green-400">{content.playCount || 0}</div>
                    </div>
                    <div className="bg-dark-900/50 rounded-lg p-4 border border-dark-600">
                      <div className="text-dark-300 text-sm mb-1">平均评分</div>
                      <div className="text-2xl font-bold text-yellow-400">{content.rating || 0}</div>
                    </div>
                  </>
                )}
              </div>

              {/* 趋势图占位符 */}
              <div className="bg-dark-900/50 rounded-lg p-6 border border-dark-600">
                <h3 className="text-lg font-bold text-white mb-4">数据趋势</h3>
                <div className="flex items-center justify-center h-32 text-dark-400">
                  <div className="text-center">
                    <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">数据趋势图功能开发中</p>
                  </div>
                </div>
              </div>

              {/* 最近评论 */}
              {comments.length > 0 && (
                <div className="bg-dark-900/50 rounded-lg p-6 border border-dark-600">
                  <h3 className="text-lg font-bold text-white mb-4">最近评论</h3>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {comments.slice(-5).reverse().map((comment) => (
                      <div key={comment.id} className="bg-dark-800 rounded-lg p-3 border border-dark-700">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-medium text-sm">{comment.username}</span>
                          <span className="text-xs text-dark-400">{formatTime(comment.timestamp)}</span>
                        </div>
                        <p className="text-dark-300 text-sm">{comment.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={() => setShowStatsModal(false)}
                className="w-full py-3 bg-primary hover:bg-primary-dark rounded-lg text-white font-medium transition-colors"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 角色创建对话框（仅世界类型） */}
      {type === 'world' && showCharacterCreation && content && (
        <CharacterCreationDialog
          world={content}
          mode={characterCreationMode}
          onModeSelect={setCharacterCreationMode}
          onComplete={handleCharacterCreated}
          onClose={() => {
            setShowCharacterCreation(false)
            setCharacterCreationMode(null)
          }}
        />
      )}
    </div>
  )
}

// 编辑内容对话框组件
function EditContentDialog({ content, type, onClose, onSave }) {
  const [editedContent, setEditedContent] = useState({ ...content })
  const [messages, setMessages] = useState([])
  const [userInput, setUserInput] = useState('')
  const [isAIThinking, setIsAIThinking] = useState(false)
  const messagesEndRef = useRef(null)

  // 自动滚动到底部
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // 初始化对话
  useEffect(() => {
    const contentTypeName = type === 'world' ? '世界' : type === 'character' ? '角色' : '故事'
    const contentName = content.name || content.title || '作品'
    
    const welcomeMsg = {
      role: 'assistant',
      content: `你好！我是你的创作助手 ✨\n\n我可以帮你优化和细化"${contentName}"这个${contentTypeName}。\n\n你可以告诉我你想要：\n- 优化某个具体部分\n- 添加更多细节\n- 改进描述\n- 或者其他任何想法\n\n请告诉我你想如何改进这个作品？`,
      timestamp: Date.now()
    }
    
    setMessages([welcomeMsg])
  }, [content, type])

  // 处理AI对话
  const handleSendMessage = async () => {
    if (!userInput.trim() || isAIThinking) return

    const userMsg = {
      role: 'user',
      content: userInput.trim(),
      timestamp: Date.now()
    }

    setMessages(prev => [...prev, userMsg])
    setUserInput('')
    setIsAIThinking(true)

    try {
      const prompt = `你是一个专业的创作助手。用户想要优化和细化他们的作品。

作品类型：${type === 'world' ? '世界' : type === 'character' ? '角色' : '故事'}

当前作品数据：
${JSON.stringify(editedContent, null, 2)}

用户的要求：
${userMsg.content}

请根据用户的要求，提供优化建议并生成改进后的内容。返回JSON格式：
{
  "message": "你的回复和建议",
  "updatedContent": {
    // 更新后的内容字段
    // 例如：name, description, summary, preText等
    // 只包含需要更新的字段
  },
  "hasUpdates": true/false
}

注意：
- 只返回JSON，不要其他文字
- updatedContent只包含实际修改的字段
- 如果用户只是在提问或讨论，hasUpdates设为false`

      const aiResponse = await RealAIService.callLanguageModel(prompt)
      const jsonMatch = aiResponse.match(/\{[\s\S]*\}/)
      
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0])
        
        // 更新编辑后的内容
        if (parsed.hasUpdates && parsed.updatedContent) {
          setEditedContent(prev => ({
            ...prev,
            ...parsed.updatedContent
          }))
        }
        
        // 添加AI回复
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: parsed.message,
          timestamp: Date.now()
        }])
      } else {
        // 如果AI没有返回JSON，直接显示回复
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: aiResponse,
          timestamp: Date.now()
        }])
      }
    } catch (error) {
      console.error('AI对话失败:', error)
      toast.error('处理失败，请重试')
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '抱歉，我遇到了一些问题。请再试一次？',
        timestamp: Date.now()
      }])
    } finally {
      setIsAIThinking(false)
    }
  }

  // 检查是否有修改
  const hasChanges = JSON.stringify(editedContent) !== JSON.stringify(content)

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-dark-800 rounded-xl w-full max-w-6xl h-[85vh] mx-4 border border-dark-600 flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-dark-700">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary" />
              编辑作品：{content.name || content.title || '未命名'}
            </h2>
            <p className="text-sm text-dark-400 mt-1">
              与AI对话来优化和细化你的作品
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-dark-700 rounded transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* 主体 - 左右分栏 */}
        <div className="flex-1 flex overflow-hidden">
          {/* 左侧：对话区 */}
          <div className="w-1/2 flex flex-col border-r border-dark-700">
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-primary/20 text-white border border-primary/30'
                        : 'bg-dark-700 text-white border border-dark-600'
                    }`}
                  >
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">{msg.content}</p>
                  </div>
                </div>
              ))}

              {isAIThinking && (
                <div className="flex justify-start">
                  <div className="rounded-lg px-4 py-3 bg-dark-700 border border-dark-600">
                    <div className="flex items-center gap-2 text-dark-300">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">AI正在思考...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* 输入区 */}
            <div className="p-6 border-t border-dark-700">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="告诉我你想如何改进..."
                  disabled={isAIThinking}
                  className="flex-1 px-4 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary disabled:opacity-50"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!userInput.trim() || isAIThinking}
                  className="px-6 py-2 bg-primary hover:bg-primary-dark rounded-lg text-white font-medium disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  发送
                </button>
              </div>
            </div>
          </div>

          {/* 右侧：预览区 */}
          <div className="w-1/2 flex flex-col bg-dark-900">
            <div className="p-6 border-b border-dark-700">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Eye className="w-5 h-5 text-secondary" />
                实时预览
                {hasChanges && (
                  <span className="ml-2 px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                    有修改
                  </span>
                )}
              </h3>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {/* 名称/标题 */}
                <div>
                  <div className="text-xs text-dark-400 mb-1">
                    {type === 'world' ? '世界名称' : type === 'character' ? '角色名称' : '故事标题'}
                  </div>
                  <div className="text-lg font-bold text-white">
                    {editedContent.name || editedContent.title || '未命名'}
                  </div>
                </div>

                {/* 描述 */}
                {editedContent.description && (
                  <div>
                    <div className="text-xs text-dark-400 mb-1">描述</div>
                    <div className="text-sm text-dark-300 leading-relaxed">
                      {editedContent.description}
                    </div>
                  </div>
                )}

                {/* 概要 */}
                {editedContent.summary && (
                  <div>
                    <div className="text-xs text-dark-400 mb-1">概要</div>
                    <div className="text-sm text-dark-300 leading-relaxed">
                      {editedContent.summary}
                    </div>
                  </div>
                )}

                {/* 世界观设定（世界类型） */}
                {type === 'world' && editedContent.preText && (
                  <div>
                    <div className="text-xs text-dark-400 mb-1">世界观设定</div>
                    <div className="text-sm text-dark-300 leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto">
                      {editedContent.preText}
                    </div>
                  </div>
                )}

                {/* 其他字段根据类型显示 */}
                {type === 'character' && editedContent.personality && (
                  <div>
                    <div className="text-xs text-dark-400 mb-1">性格</div>
                    <div className="text-sm text-dark-300 leading-relaxed">
                      {editedContent.personality}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 底部操作栏 */}
        <div className="p-6 border-t border-dark-700 bg-dark-800/50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-dark-400">
              {hasChanges ? (
                <span className="text-yellow-400">⚠️ 有未保存的修改</span>
              ) : (
                <span>没有修改</span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg font-medium transition-colors"
              >
                取消
              </button>
              <button
                onClick={() => onSave(editedContent, 'draft')}
                disabled={!hasChanges}
                className="px-6 py-2 bg-dark-600 hover:bg-dark-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                保存到草稿
              </button>
              <button
                onClick={() => onSave(editedContent, 'publish')}
                disabled={!hasChanges}
                className="px-6 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                发布更新
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// 角色创建对话框组件（从 WorldDetail.jsx 复制并简化）
function CharacterCreationDialog({ world, mode, onModeSelect, onComplete, onClose }) {
  const [characterData, setCharacterData] = useState({
    name: '',
    role: '',
    background: '',
    personality: '',
    goals: ''
  })
  const [aiMessages, setAiMessages] = useState([])
  const [userInput, setUserInput] = useState('')
  const [isAIThinking, setIsAIThinking] = useState(false)
  const [hasAnnouncedCompletion, setHasAnnouncedCompletion] = useState(false)

  const characterFields = [
    { key: 'name', label: '姓名', required: true },
    { key: 'role', label: '身份/职业', required: false },
    { key: 'background', label: '背景故事', required: false },
    { key: 'personality', label: '性格特点', required: false },
    { key: 'goals', label: '目标/动机', required: false }
  ]

  const completedFields = characterFields.filter(field => characterData[field.key]?.trim()).length
  const requiredCompleted = characterFields.filter(field => field.required && characterData[field.key]?.trim()).length
  const totalRequired = characterFields.filter(field => field.required).length
  const isCharacterComplete = requiredCompleted === totalRequired

  const getMissingFields = (data) => {
    return characterFields
      .filter(field => field.required)
      .filter(field => !data[field.key]?.trim())
      .map(field => field.key)
  }

  const summarizeCharacter = (data) => {
    return `角色档案：
• 姓名：${data.name}
• 身份/职业：${data.role || '未填写'}
• 背景：${data.background || '未填写'}
• 性格特点：${data.personality || '未填写'}
• 目标/动机：${data.goals || '未填写'}

确认无误即可开始体验。`
  }

  const extractCharacterInfo = (input, prevData) => {
    const updates = {}

    if (!prevData.name?.trim()) {
      const namePatterns = [
        /(?:我叫|我的名字叫|我的名字是|我的姓名是|叫我|我是)(?:叫|是)?\s*([^\s，,。！!？?\n]{1,20})/,
        /(?:角色名|姓名|名字|名称)\s*[：:]\s*([^\n，,。！!？?]+)/,
        /(?:角色名称是)\s*[：:]\s*([^\n，,。！!？?]+)/,
        /^([^\n，,。！!？?]{1,10})$/
      ]
      for (const pattern of namePatterns) {
        const match = input.match(pattern)
        if (match && match[1]) {
          let extractedName = match[1].trim()
          extractedName = extractedName.replace(/[，,。！!？?\s].*$/, '').trim()
          if (extractedName.length > 0 && extractedName.length <= 20) {
            updates.name = extractedName
            break
          }
        }
      }
    }

    const fieldPatterns = {
      role: [
        /(?:身份|职业|角色)[：:]\s*([^\n，,。！!？?]+)/,
        /(?:扮演|担任|作为)(?:一名|一个|一位)?([^\n，,。！!？?]+)/,
        /我是(?:一名|一个|一位)?([^\n，,。！!？?]+)/,
      ],
      background: [
        /(?:背景|出身|来历|过去|经历)[：:]\s*([^\n]+)/,
        /来自([^\n，,。！!？?]+)/,
      ],
      personality: [
        /(?:性格|特点|个性|风格)[：:]\s*([^\n]+)/,
        /我是一个([^，。,。！!？?\n]+)/,
      ],
      goals: [
        /(?:目标|动机|愿望|目的|梦想)[：:]\s*([^\n]+)/,
        /我想要([^，。,。！!？?\n]+)/,
      ]
    }

    Object.entries(fieldPatterns).forEach(([key, patterns]) => {
      if (prevData[key]?.trim() || updates[key]) return
      for (const pattern of patterns) {
        const match = input.match(pattern)
        if (match && match[1]) {
          const extracted = match[1].trim()
          if (extracted) {
            updates[key] = extracted
            break
          }
        }
      }
    })

    return Object.keys(updates).length > 0 ? { ...prevData, ...updates } : prevData
  }

  const applyExtractedData = (input) => {
    let updatedSnapshot = characterData
    setCharacterData(prev => {
      const updated = extractCharacterInfo(input, prev)
      updatedSnapshot = updated
      return updated
    })
    return updatedSnapshot
  }

  // 初始化AI对话
  useEffect(() => {
    if (mode === 'ai' && aiMessages.length === 0) {
      const welcomeMsg = {
        role: 'assistant',
        content: `你好！我是你的角色创建助手。\n\n让我们一起来创建你在"${world.name}"中的角色。\n\n首先，请告诉我你的角色姓名，或者你想扮演什么样的角色？`,
        timestamp: Date.now()
      }
      setAiMessages([welcomeMsg])
    }
  }, [mode, world.name])

  useEffect(() => {
    const missing = getMissingFields(characterData)
    if (missing.length === 0 && !hasAnnouncedCompletion && characterData.name.trim()) {
      setHasAnnouncedCompletion(true)
      setAiMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: `太好了！你的角色已经准备好。\n\n${summarizeCharacter(characterData)}\n\n确认后即可进入世界冒险。`,
          timestamp: Date.now()
        }
      ])
    }
    if (missing.length > 0 && hasAnnouncedCompletion) {
      setHasAnnouncedCompletion(false)
    }
  }, [characterData, hasAnnouncedCompletion])

  // 处理AI对话
  const handleAISend = async () => {
    if (!userInput.trim() || isAIThinking) return

    const currentInput = userInput.trim()
    const userMsg = {
      role: 'user',
      content: currentInput,
      timestamp: Date.now()
    }

    setAiMessages(prev => [...prev, userMsg])
    setUserInput('')
    setIsAIThinking(true)

    const dataAfterUser = applyExtractedData(currentInput)
    const missingAfterUser = getMissingFields(dataAfterUser)

    try {
      const prompt = `你是角色创建助手，正在帮助玩家在"${world.name}"中创建角色。

世界设定（节选）：
${world.preText ? world.preText.substring(0, 500) : world.description || world.summary || ''}

当前对话历史：
${aiMessages.map(m => `${m.role === 'user' ? '玩家' : '助手'}: ${m.content}`).join('\n')}

玩家最新输入：${currentInput}

当前角色信息：
${JSON.stringify(dataAfterUser, null, 2)}

仍缺少字段：${missingAfterUser.length > 0 ? missingAfterUser.map(key => characterFields.find(f => f.key === key)?.label || key).join('、') : '无（已经完整）'}

规则：
1. 只需完成姓名、身份、背景、性格、目标五项，其他细节可留空。
2. 如果仍缺字段，只针对一个缺失点提出一个简短问题，引导玩家补充。
3. 如果信息已经完整，直接总结角色并提示可以开始体验。
4. 始终使用玩家提供的姓名，禁止改名或创造新身份。

请根据以上要求输出回复。`

      const response = await RealAIService.callLanguageModel(prompt)
      const dataAfterAI = applyExtractedData(response)

      setAiMessages(prev => [...prev, {
        role: 'assistant',
        content: response,
        timestamp: Date.now()
      }])

      const remaining = getMissingFields(dataAfterAI)
      if (remaining.length === 0 && !hasAnnouncedCompletion && dataAfterAI.name?.trim()) {
        setAiMessages(prev => [...prev, {
          role: 'assistant',
          content: summarizeCharacter(dataAfterAI),
          timestamp: Date.now()
        }])
        setHasAnnouncedCompletion(true)
      }
    } catch (error) {
      console.error('AI对话失败:', error)
      toast.error('对话失败，请重试')
    } finally {
      setIsAIThinking(false)
    }
  }

  // 模板输入模式
  const handleTemplateSubmit = () => {
    if (!characterData.name.trim()) {
      toast.error('请输入角色姓名')
      return
    }
    onComplete(characterData)
  }

  // 确认AI创建的角色
  const handleAIConfirm = () => {
    const missing = getMissingFields(characterData)
    if (missing.length > 0) {
      toast.error('角色信息不完整，请补充：' + missing.map(key => characterFields.find(f => f.key === key)?.label || key).join('、'))
      return
    }
    onComplete(characterData)
  }

  // 模式选择界面
  if (!mode) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-dark-800 rounded-xl p-8 max-w-2xl w-full mx-4 border border-dark-600">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">创建你的角色</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-dark-700 rounded transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          <p className="text-dark-300 mb-6">
            选择一种方式创建你在 <span className="text-primary">{world.name}</span> 中的角色
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => onModeSelect('ai')}
              className="bg-dark-700 hover:bg-dark-600 border-2 border-dark-600 hover:border-primary/50 rounded-lg p-6 text-left transition-all group"
            >
              <MessageCircle className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-primary transition-colors">
                AI对话创建
              </h3>
              <p className="text-dark-300 text-sm">
                通过自然对话，AI引导你逐步完善角色设定
              </p>
            </button>

            <button
              onClick={() => onModeSelect('template')}
              className="bg-dark-700 hover:bg-dark-600 border-2 border-dark-600 hover:border-secondary/50 rounded-lg p-6 text-left transition-all group"
            >
              <FileText className="w-12 h-12 text-secondary mb-4" />
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-secondary transition-colors">
                模板填写
              </h3>
              <p className="text-dark-300 text-sm">
                快速填写角色信息表单，直接创建角色
              </p>
            </button>
          </div>
        </div>
      </div>
    )
  }

  // AI对话模式
  if (mode === 'ai') {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-dark-800 rounded-xl w-full max-w-4xl h-[80vh] mx-4 border border-dark-600 flex flex-col">
          <div className="flex items-center justify-between p-6 border-b border-dark-700">
            <h2 className="text-xl font-bold text-white">AI对话创建角色</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-dark-700 rounded transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          <div className="px-6 pt-4 pb-2 border-b border-dark-700">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-white font-medium">角色设定进度</span>
              <span className="text-xs text-primary">
                {completedFields}/{characterFields.length} 已填写
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {characterFields.map(field => {
                const filled = !!characterData[field.key]?.trim()
                return (
                  <span
                    key={field.key}
                    className={`px-3 py-1 rounded-full text-xs border ${
                      filled
                        ? 'bg-primary/15 border-primary/40 text-primary'
                        : 'bg-dark-700 border-dark-600 text-dark-400'
                    }`}
                  >
                    {filled ? '✓ ' : ''}
                    {field.label}
                    {field.required && <span className="text-red-400 ml-1">*</span>}
                  </span>
                )
              })}
            </div>
            <div className="mt-3 h-1.5 bg-dark-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary via-secondary to-primary transition-all duration-500"
                style={{ width: `${(completedFields / characterFields.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {aiMessages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-primary/20 text-white'
                      : 'bg-dark-700 text-white'
                  }`}
                >
                  <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                </div>
              </div>
            ))}

            {isAIThinking && (
              <div className="flex justify-start">
                <div className="rounded-lg px-4 py-3 bg-dark-700">
                  <div className="flex items-center gap-2 text-dark-300">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">AI正在思考...</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-6 border-t border-dark-700">
            <div className="flex gap-2">
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAISend()}
                placeholder="输入你的回答..."
                disabled={isAIThinking}
                className="flex-1 px-4 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary"
              />
              <button
                onClick={handleAISend}
                disabled={!userInput.trim() || isAIThinking}
                className="px-6 py-2 bg-primary hover:bg-primary-dark rounded-lg text-white font-medium disabled:opacity-50 transition-colors"
              >
                发送
              </button>
            </div>
            <button
              onClick={handleAIConfirm}
              disabled={!isCharacterComplete}
              className={`w-full mt-4 py-2 rounded-lg font-medium transition-colors ${
                isCharacterComplete
                  ? 'bg-secondary hover:bg-secondary-dark text-white'
                  : 'bg-dark-600 text-dark-300 cursor-not-allowed'
              }`}
            >
              {isCharacterComplete ? '确认并开始旅程' : '角色信息未完成（至少填写姓名）'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // 模板填写模式
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-dark-800 rounded-xl p-8 max-w-2xl w-full mx-4 border border-dark-600 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">模板填写创建角色</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-dark-700 rounded transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              角色姓名 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={characterData.name}
              onChange={(e) => setCharacterData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="输入角色姓名"
              className="w-full px-4 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              角色身份/职业
            </label>
            <input
              type="text"
              value={characterData.role}
              onChange={(e) => setCharacterData(prev => ({ ...prev, role: e.target.value }))}
              placeholder="例如：游侠、商人、学者等"
              className="w-full px-4 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              背景故事
            </label>
            <textarea
              value={characterData.background}
              onChange={(e) => setCharacterData(prev => ({ ...prev, background: e.target.value }))}
              placeholder="描述角色的出身、经历等"
              rows={4}
              className="w-full px-4 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              性格特点
            </label>
            <textarea
              value={characterData.personality}
              onChange={(e) => setCharacterData(prev => ({ ...prev, personality: e.target.value }))}
              placeholder="描述角色的性格、行为特点等"
              rows={3}
              className="w-full px-4 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              目标/动机
            </label>
            <textarea
              value={characterData.goals}
              onChange={(e) => setCharacterData(prev => ({ ...prev, goals: e.target.value }))}
              placeholder="描述角色在这个世界中的目标和动机"
              rows={3}
              className="w-full px-4 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary resize-none"
            />
          </div>
        </div>

        <div className="flex gap-4 mt-6">
          <button
            onClick={onClose}
            className="flex-1 py-2 bg-dark-700 hover:bg-dark-600 rounded-lg text-white font-medium transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleTemplateSubmit}
            className="flex-1 py-2 bg-primary hover:bg-primary-dark rounded-lg text-white font-medium transition-colors"
          >
            创建角色并开始
          </button>
        </div>
      </div>
    </div>
  )
}

